import OrderedList from "@tiptap/extension-ordered-list";
import BulletList from "@tiptap/extension-bullet-list";
import ListItem from "@tiptap/extension-list-item";
import { CommandProps } from "@tiptap/core";
import { Plugin, PluginKey, TextSelection } from "@tiptap/pm/state";

export const CustomOrderedList = OrderedList.extend({
  addAttributes() {
    return {
      // @ts-ignore - TipTap's parent() method exists at runtime but not in types
      ...this.parent?.(),
      listStyleType: {
        default: null, // default fallback
        parseHTML: (element: HTMLElement) => element.style.listStyleType,
        renderHTML: (attributes: { listStyleType?: string | null }) => {
          if (!attributes.listStyleType) {
            return {}; // Return empty object = no style attribute
          }
          return {
            style: `list-style-type: ${attributes.listStyleType}`,
          };
        },
      },
    };
  },

  addCommands() {
    return {
      toggleOrderedList:
        () =>
        ({ commands, state, chain }: CommandProps) => {
          const { $from } = state.selection;
          const node = $from.node($from.depth);

          // Check if we're currently in an ordered list (to detect if we're turning it off)
          const isInOrderedList = this.editor.isActive("orderedList");

          if (isInOrderedList) {
            if (node && node.type.name === "doc") {
              const orderedList = node.firstChild;
              if (orderedList && orderedList.type.name === "orderedList") {
                // Selection from the start of the first list item to the end of the last one
                const from = 2; // doc + orderedList
                const to = from + orderedList.content.size;

                return chain()
                  .setTextSelection({ from, to })
                  .toggleList(
                    this.name,
                    this.options.itemTypeName,
                    this.options.keepMarks
                  )
                  .setNode("paragraph")
                  .run();
              }
            }
            // If we're turning OFF the list and current node is a div, convert back to paragraph
            return chain()
              .toggleList(
                this.name,
                this.options.itemTypeName,
                this.options.keepMarks
              )
              .setNode("paragraph")
              .run();
          }

          // If it's a paragraph, change it to div before applying list
          if (node && node.type.name === "paragraph") {
            const genericBlockInline = state.schema.nodes.genericBlockInline;
            if (genericBlockInline) {
              return chain()
                .setNode("genericBlockInline", { tag: "div" })
                .toggleList(
                  this.name,
                  this.options.itemTypeName,
                  this.options.keepMarks
                )
                .updateAttributes(
                  "listItem",
                  this.editor.getAttributes("textStyle")
                )
                .run();
            }
          }

          if (this.options.keepAttributes) {
            return chain()
              .toggleList(
                this.name,
                this.options.itemTypeName,
                this.options.keepMarks
              )
              .updateAttributes(
                "listItem",
                this.editor.getAttributes("textStyle")
              )
              .run();
          }
          return commands.toggleList(
            this.name,
            this.options.itemTypeName,
            this.options.keepMarks
          );
        },
    };
  },
});

export const CustomBulletList = BulletList.extend({
  addAttributes() {
    return {
      // @ts-ignore - TipTap's parent() method exists at runtime but not in types
      ...this.parent?.(),
      listStyleType: {
        default: null, // default fallback
        parseHTML: (element: HTMLElement) => element.style.listStyleType,
        renderHTML: (attributes: { listStyleType?: string | null }) => {
          if (!attributes.listStyleType) {
            return {}; // Return empty object = no style attribute
          }
          return {
            style: `list-style-type: ${attributes.listStyleType}`,
          };
        },
      },
    };
  },

  addCommands() {
    return {
      toggleBulletList:
        () =>
        ({ commands, state, chain }: CommandProps) => {
          const { $from } = state.selection;
          const node = $from.node($from.depth);

          // Check if we're currently in a bullet list (to detect if we're turning it off)
          const isInBulletList = this.editor.isActive("bulletList");

          if (isInBulletList) {
            if (node && node.type.name === "doc") {
              const bulletList = node.firstChild;
              if (bulletList && bulletList.type.name === "bulletList") {
                // Selection from the start of the first list item to the end of the last one
                const from = 2; // doc + bulletList
                const to = from + bulletList.content.size;

                return chain()
                  .setTextSelection({ from, to })
                  .toggleList(
                    this.name,
                    this.options.itemTypeName,
                    this.options.keepMarks
                  )
                  .setNode("paragraph")
                  .run();
              }
            }
            // If we're turning OFF the list and current node is a div, convert back to paragraph
            return chain()
              .toggleList(
                this.name,
                this.options.itemTypeName,
                this.options.keepMarks
              )
              .setNode("paragraph")
              .run();
          }

          // If it's a paragraph, change it to div before applying list
          if (node && node.type.name === "paragraph") {
            const genericBlockInline = state.schema.nodes.genericBlockInline;
            if (genericBlockInline) {
              return chain()
                .setNode("genericBlockInline", { tag: "div" })
                .toggleList(
                  this.name,
                  this.options.itemTypeName,
                  this.options.keepMarks
                )
                .updateAttributes(
                  "listItem",
                  this.editor.getAttributes("textStyle")
                )
                .run();
            }
          }

          if (this.options.keepAttributes) {
            return chain()
              .toggleList(
                this.name,
                this.options.itemTypeName,
                this.options.keepMarks
              )
              .updateAttributes(
                "listItem",
                this.editor.getAttributes("textStyle")
              )
              .run();
          }

          return commands.toggleList(
            this.name,
            this.options.itemTypeName,
            this.options.keepMarks
          );
        },
    };
  },
});

export const CustomLists = ListItem.extend({
  content: "block*",

  addKeyboardShortcuts() {
    return {
      ...this.parent?.(),
      Enter: () => {
        const { state } = this.editor;
        const { $from } = state.selection;

        // Check if we're in a list item
        let inListItem = false;
        for (let d = $from.depth; d > 0; d--) {
          if ($from.node(d).type.name === "listItem") {
            inListItem = true;
            break;
          }
        }

        if (!inListItem) {
          return false;
        }

        // Use default splitListItem behavior, then convert p to div
        const result = this.editor.commands.splitListItem("listItem");

        if (result) {
          // After split, check if the new node is a paragraph and convert to div
          const { state: newState } = this.editor;
          const { $from: new$from } = newState.selection;
          const currentNode = new$from.parent;

          if (currentNode.type.name === "paragraph") {
            this.editor.commands.setNode("genericBlockInline", { tag: "div" });
          }
        }

        return true;
      },
      Backspace: () => {
        const { state, view } = this.editor;
        const { $from, empty } = state.selection;

        // If selection is not empty, allow default behavior
        if (!empty) {
          return false;
        }

        // Find listItem and list in the current position
        let listItemDepth = -1;
        let listDepth = -1;
        for (let d = $from.depth; d > 0; d--) {
          const node = $from.node(d);
          if (node.type.name === "listItem" && listItemDepth === -1) {
            listItemDepth = d;
          }
          if (
            (node.type.name === "bulletList" ||
              node.type.name === "orderedList") &&
            listDepth === -1
          ) {
            listDepth = d;
          }
        }

        // Only proceed if we're actually in a list
        if (listItemDepth === -1 || listDepth === -1) {
          return false;
        }

        const listItem = $from.node(listItemDepth);
        const list = $from.node(listDepth);

        // Find block element inside listItem
        const blockNode = listItem.firstChild;
        if (!blockNode) {
          return false;
        }

        // Check if this is the only list item in the list
        const isOnlyListItem = list.childCount === 1;

        // Check if this is the first list item
        const listItemIndex = list.content.content.findIndex(
          (child: any) => child === listItem
        );
        const isFirstListItem = listItemIndex === 0;

        // Check if block element is empty
        const isBlockEmpty = blockNode.content.size === 0;

        // Check cursor position
        const blockStart = $from.start(listItemDepth + 1);
        const isAtBlockStart = $from.pos === blockStart;

        // If block has content (not empty)
        if (!isBlockEmpty) {
          // If we're NOT at the start, just delete one character before cursor
          if (!isAtBlockStart) {
            const { tr } = state;
            // Delete one character before cursor position
            const deleteFrom = $from.pos - 1;
            const deleteTo = $from.pos;
            const transaction = tr.delete(deleteFrom, deleteTo);
            view.dispatch(transaction);
            return true; // Prevent parent handler
          }
          // If we ARE at start of non-empty block, use default behavior
          return false;
        }

        // From here on, block is empty
        // Special handling ONLY for empty blocks at the start
        if (!isAtBlockStart) {
          // Empty block but not at start? This shouldn't happen, but use default
          return false;
        }

        // Now we know: block is empty AND cursor is at block start
        const { tr } = state;

        // Check if this list is nested inside another list item
        const parentDepth = listDepth - 1;
        let isNestedList = false;
        if (parentDepth > 0) {
          const parentNode = $from.node(parentDepth);
          if (parentNode && parentNode.type.name === "listItem") {
            isNestedList = true;
          }
        }

        if (isOnlyListItem && isNestedList) {
          // This is the only item in a nested list
          // Check if current listItem has its own nested list that needs to be preserved
          const hasOwnNestedList = listItem.childCount > 1 &&
            (listItem.child(1).type.name === "bulletList" ||
             listItem.child(1).type.name === "orderedList");

          if (hasOwnNestedList) {
            // Current item has its own nested list - lift the entire listItem preserving nested list
            // Replace the wrapper list with just the listItem content (will be placed in parent list)
            const listPos = $from.before(listDepth);
            const listEnd = listPos + list.nodeSize;

            // Replace the entire nested list with the listItem (preserving its nested list)
            let transaction = tr.replaceWith(listPos, listEnd, listItem);

            // Position cursor in the div inside the listItem
            const newPos = listPos + 2; // +1 for listItem, +1 to be inside the div
            const $newPos = transaction.doc.resolve(newPos);
            transaction.setSelection(TextSelection.near($newPos));

            view.dispatch(transaction);
            return true;
          }

          // No nested list - original behavior
          // We should lift it to the parent list level
          const listPos = $from.before(listDepth);
          const listEnd = listPos + list.nodeSize;

          // Create a new list item for the parent list
          const newListItem = state.schema.nodes.listItem.create(
            null,
            state.schema.nodes.genericBlockInline.create({ tag: "div" })
          );

          // Replace the entire nested list with the new list item
          let transaction = tr.replaceWith(listPos, listEnd, newListItem);

          // Position cursor in the new list item
          const newPos = listPos + 2; // +1 for listItem, +1 to be inside the div
          const $newPos = transaction.doc.resolve(newPos);
          transaction.setSelection(TextSelection.near($newPos));

          view.dispatch(transaction);
          return true;
        } else if (isFirstListItem && isNestedList) {
          // First item (but not only) in a nested list
          // Check if current listItem has its own nested list that needs to be preserved
          const hasOwnNestedList = listItem.childCount > 1 &&
            (listItem.child(1).type.name === "bulletList" ||
             listItem.child(1).type.name === "orderedList");

          if (hasOwnNestedList) {
            // Current item has its own nested list (e.g., empty <li> with nested <ul>)
            // We need to lift this listItem to the parent list level
            // and merge remaining siblings into its nested list

            // Structure:
            // grandparentList > parentListItem > currentList > [liftedLi(+nestedList), remainingLi...]
            // We want:
            // grandparentList > [parentListItem(without currentList), liftedLi(nestedList + remainingLi)]

            // Find the grandparent list (the one containing parentListItem)
            let grandparentListDepth = -1;
            for (let d = parentDepth - 1; d > 0; d--) {
              const node = $from.node(d);
              if (node.type.name === "bulletList" || node.type.name === "orderedList") {
                grandparentListDepth = d;
                break;
              }
            }

            if (grandparentListDepth === -1) {
              // No grandparent list, fall back to default
              return this.editor.commands.liftListItem("listItem");
            }

            const grandparentList = $from.node(grandparentListDepth);
            const grandparentListPos = $from.before(grandparentListDepth);
            const parentListItemNode = $from.node(parentDepth);
            const parentListItemIndex = $from.index(grandparentListDepth);

            // Get the nested list from our listItem
            const ownNestedList = listItem.child(1);

            // Collect remaining items from current list (siblings after liftedLi)
            const remainingCurrentListItems: any[] = [];
            for (let i = 1; i < list.childCount; i++) {
              remainingCurrentListItems.push(list.child(i));
            }

            // Build new nested list content: original nested items + remaining siblings
            const newNestedListContent: any[] = [];
            ownNestedList.forEach((child: any) => {
              newNestedListContent.push(child);
            });
            remainingCurrentListItems.forEach((item: any) => {
              newNestedListContent.push(item);
            });

            // Create new nested list with merged content
            const newNestedList = ownNestedList.type.create(ownNestedList.attrs, newNestedListContent);

            // Create the lifted listItem with the merged nested list
            const liftedListItemContent: any[] = [listItem.firstChild!, newNestedList];
            const liftedListItem = listItem.type.create(listItem.attrs, liftedListItemContent);

            // Build modified parent listItem (without the current nested list)
            const modifiedParentListItemContent: any[] = [];
            parentListItemNode.forEach((child: any) => {
              if (child !== list) {
                modifiedParentListItemContent.push(child);
              }
            });
            const modifiedParentListItem = parentListItemNode.type.create(
              parentListItemNode.attrs,
              modifiedParentListItemContent
            );

            // Build new grandparent list content
            const newGrandparentListContent: any[] = [];
            for (let i = 0; i < grandparentList.childCount; i++) {
              if (i === parentListItemIndex) {
                // Replace parent list item with modified version + lifted item
                newGrandparentListContent.push(modifiedParentListItem);
                newGrandparentListContent.push(liftedListItem);
              } else {
                newGrandparentListContent.push(grandparentList.child(i));
              }
            }

            // Create new grandparent list
            const newGrandparentList = grandparentList.type.create(
              grandparentList.attrs,
              newGrandparentListContent
            );

            // Replace grandparent list
            const grandparentListEnd = grandparentListPos + grandparentList.nodeSize;
            let transaction = tr.replaceWith(grandparentListPos, grandparentListEnd, newGrandparentList);

            // Position cursor in the lifted item's div
            let cursorPos = grandparentListPos + 1; // enter grandparent list
            for (let i = 0; i <= parentListItemIndex; i++) {
              if (i < parentListItemIndex) {
                cursorPos += grandparentList.child(i).nodeSize;
              } else {
                cursorPos += modifiedParentListItem.nodeSize;
              }
            }
            cursorPos += 2; // enter liftedListItem + enter div

            const $newPos = transaction.doc.resolve(cursorPos);
            transaction.setSelection(TextSelection.near($newPos));

            view.dispatch(transaction);
            return true;
          } else {
            // No own nested list - use liftListItem and convert p to div
            const result = this.editor.commands.liftListItem("listItem");

            if (result) {
              // After lifting, check if the node is a paragraph and convert to div
              const { state: newState } = this.editor;
              const { $from: new$from } = newState.selection;
              const currentNode = new$from.parent;

              if (currentNode.type.name === "paragraph") {
                this.editor.commands.setNode("genericBlockInline", { tag: "div" });
              }
            }

            return true;
          }
        } else if (isOnlyListItem) {
          // If it's the only item, replace entire list with paragraph
          const listPos = $from.before(listDepth);
          const listEnd = listPos + list.nodeSize;

          // Create paragraph node
          const newNode = state.schema.nodes.paragraph.create();

          // Replace entire list with new node
          const transaction = tr.replaceWith(listPos, listEnd, newNode);

          // Set cursor inside
          const newPos = listPos + 1;
          const $newPos = transaction.doc.resolve(newPos);
          transaction.setSelection(TextSelection.near($newPos));

          view.dispatch(transaction);
          return true;
        } else if (isFirstListItem) {
          // If it's the first item of many, create paragraph before list and remove first item
          // BUT preserve nested children by promoting them to top level
          const listPos = $from.before(listDepth);
          const listItemPos = $from.before(listItemDepth);

          // Check if current listItem has a nested list that needs to be preserved
          const hasNestedList = listItem.childCount > 1 &&
            (listItem.child(1).type.name === "bulletList" ||
             listItem.child(1).type.name === "orderedList");

          // Create paragraph node
          const newNode = state.schema.nodes.paragraph.create();

          if (hasNestedList) {
            // Get the nested list children - they should be promoted to top level
            const nestedList = listItem.child(1);
            const nestedListItems: any[] = [];
            nestedList.forEach((child: any) => {
              nestedListItems.push(child);
            });

            // Collect remaining siblings (items after the first one)
            const remainingSiblings: any[] = [];
            for (let i = 1; i < list.childCount; i++) {
              remainingSiblings.push(list.child(i));
            }

            // Build new list content: nested items promoted + remaining siblings
            const newListContent = [...nestedListItems, ...remainingSiblings];

            // Create new list with promoted items
            const newList = list.type.create(list.attrs, newListContent);

            // Replace entire list with paragraph + new list
            const listEnd = listPos + list.nodeSize;
            let transaction = tr.replaceWith(listPos, listEnd, [newNode, newList]);

            // Set cursor inside the new paragraph
            const newPos = listPos + 1;
            const $newPos = transaction.doc.resolve(newPos);
            transaction.setSelection(TextSelection.near($newPos));

            view.dispatch(transaction);
            return true;
          }

          // No nested list - original behavior
          // First insert paragraph before the list
          let transaction = tr.insert(listPos, newNode);

          // Then delete the empty first list item (adjust position after insert)
          const adjustedListItemPos = listItemPos + newNode.nodeSize;
          const adjustedListItemEnd = adjustedListItemPos + listItem.nodeSize;
          transaction = transaction.delete(
            adjustedListItemPos,
            adjustedListItemEnd
          );

          // Set cursor inside the new paragraph
          const newPos = listPos + 1;
          const $newPos = transaction.doc.resolve(newPos);
          transaction.setSelection(TextSelection.near($newPos));

          view.dispatch(transaction);
          return true;
        } else if (!isFirstListItem) {
          // Middle or last item in a list (nested or not)
          // Check if current listItem has a nested list that needs to be preserved
          const hasNestedList = listItem.childCount > 1 &&
            (listItem.child(1).type.name === "bulletList" ||
             listItem.child(1).type.name === "orderedList");

          const listItemPos = $from.before(listItemDepth);
          const listItemEnd = listItemPos + listItem.nodeSize;

          // Get the previous list item
          const $listItemStart = state.doc.resolve(listItemPos);
          const prevListItemIndex = $listItemStart.index(listDepth) - 1;
          const prevListItem = list.child(prevListItemIndex);
          const prevListItemPos = listItemPos - prevListItem.nodeSize;

          if (hasNestedList) {
            // Current item has nested list - move it to previous item
            const nestedList = listItem.child(1);

            // Find the deepest last position in the previous listItem's nested structure
            const findDeepestLastPosition = (li: any, startPos: number): number => {
              // Check if this listItem has a nested list
              if (li.childCount > 1) {
                const maybeNestedList = li.child(1);
                if (maybeNestedList.type.name === "bulletList" || maybeNestedList.type.name === "orderedList") {
                  // Has nested list - recurse into the last item of that nested list
                  const lastNestedItemIndex = maybeNestedList.childCount - 1;
                  const lastNestedItem = maybeNestedList.child(lastNestedItemIndex);

                  // Calculate position of last nested item
                  let nestedItemPos = startPos + 1 + li.firstChild!.nodeSize + 1; // +1 for listItem, +firstBlock size, +1 for nested list
                  for (let i = 0; i < lastNestedItemIndex; i++) {
                    nestedItemPos += maybeNestedList.child(i).nodeSize;
                  }

                  return findDeepestLastPosition(lastNestedItem, nestedItemPos);
                }
              }
              // No nested list - return end of first block in this listItem
              const firstBlock = li.firstChild;
              return startPos + 1 + 1 + (firstBlock ? firstBlock.content.size : 0);
            };

            const cursorPos = findDeepestLastPosition(prevListItem, prevListItemPos);

            // Calculate where to insert nested list (at end of previous listItem)
            const prevListItemEnd = listItemPos; // Previous listItem ends where current starts
            const insertPos = prevListItemEnd - 1; // -1 to be inside the listItem

            // Build transaction:
            // 1. Delete the current listItem entirely
            // 2. Insert the nested list at end of previous listItem
            let transaction = tr.delete(listItemPos, listItemEnd);

            // After deletion, insertPos needs adjustment
            // Since we deleted after insertPos, no adjustment needed
            transaction = transaction.insert(insertPos, nestedList);

            // Set cursor position (before the deletion, so no adjustment needed)
            const $newPos = transaction.doc.resolve(cursorPos);
            transaction.setSelection(TextSelection.near($newPos, -1));

            view.dispatch(transaction);
            return true;
          } else {
            // No nested list - simple delete and move cursor to end of deepest nested item in previous
            const findDeepestLastPosition = (li: any, startPos: number): number => {
              if (li.childCount > 1) {
                const maybeNestedList = li.child(1);
                if (maybeNestedList.type.name === "bulletList" || maybeNestedList.type.name === "orderedList") {
                  const lastNestedItemIndex = maybeNestedList.childCount - 1;
                  const lastNestedItem = maybeNestedList.child(lastNestedItemIndex);

                  let nestedItemPos = startPos + 1 + li.firstChild!.nodeSize + 1;
                  for (let i = 0; i < lastNestedItemIndex; i++) {
                    nestedItemPos += maybeNestedList.child(i).nodeSize;
                  }

                  return findDeepestLastPosition(lastNestedItem, nestedItemPos);
                }
              }
              const firstBlock = li.firstChild;
              return startPos + 1 + 1 + (firstBlock ? firstBlock.content.size : 0);
            };

            const cursorPos = findDeepestLastPosition(prevListItem, prevListItemPos);

            // First set cursor to target position, then delete
            let transaction = tr.setSelection(TextSelection.create(state.doc, cursorPos));

            // Delete the current empty list item
            transaction = transaction.delete(listItemPos, listItemEnd);

            view.dispatch(transaction);
            return true;
          }
        }

        // Fallback - use default behavior
        return false;
      },
    };
  },
});
